import React, { useEffect, useState } from "react";
import { getSleeveFilesList, addSleeveFilesKeywords, deleteSleeveFilesKeywords, updateSleeveFilesDocumentsType } from "../../../services/AdminService";
import { FaTimes } from "react-icons/fa";
import { Pagination } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";

const MyFilesTable = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState({});
  const [tagModal, setTagModal] = useState(false);
  const [docTypeModal, setDocTypeModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [modalKeywords, setModalKeywords] = useState([]);
  const [tagInputs, setTagInputs] = useState({});
  const [errorMessages, setErrorMessages] = useState({});
  const [docTypeInput, setDocTypeInput] = useState("");
  const MAX_KEYWORDS = 15;

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const response = await getSleeveFilesList();
      if (response.data.message === "Success") {
        const formattedFiles = response.data.data.map(file => ({
          ...file,
          keywords: Array.isArray(file.keywords) ? file.keywords : file.keywords ? file.keywords.split(",").map(k => k.trim()) : []
        }));
        setFiles(formattedFiles);
      } else {
        console.error("Unexpected API response format:", response);
        setFiles([]);
      }
    } catch (error) {
      console.error("Error fetching files:", error);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = files.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(files.length / itemsPerPage);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const toggleExpand = (fileId) => setExpandedRows(prev => ({ ...prev, [fileId]: !prev[fileId] }));

  const handleOpenTagModal = (file) => {
    setSelectedFile(file);
    const keywords = Array.isArray(file.keywords) ? file.keywords : file.keywords ? file.keywords.split(",").map(k => k.trim()) : [];
    setModalKeywords(keywords);
    setTagModal(true);
  };

  const handleCloseTagModal = () => {
    setTagModal(false);
    setSelectedFile(null);
    setModalKeywords([]);
    setTagInputs({});
    setErrorMessages({});
  };

  const handleOpenDocTypeModal = (file) => {
    setSelectedFile(file);
    setDocTypeInput(file.document_type || "");
    setDocTypeModal(true);
  };

  const handleCloseDocTypeModal = () => {
    setDocTypeModal(false);
    setSelectedFile(null);
    setDocTypeInput("");
    setErrorMessages({});
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && selectedFile) {
      saveKeyword(selectedFile.id, tagInputs[selectedFile.id]);
    }
  };

  const saveKeyword = async (fileId, keywordValue) => {
    const trimmedKeyword = keywordValue?.trim();
    if (!trimmedKeyword) {
      setErrorMessages(prev => ({ ...prev, [fileId]: "Keyword cannot be empty!" }));
      return;
    }

    if (modalKeywords.includes(trimmedKeyword)) {
      setErrorMessages(prev => ({ ...prev, [fileId]: "This keyword already exists!" }));
      return;
    }

    if (modalKeywords.length >= MAX_KEYWORDS) {
      setErrorMessages(prev => ({ ...prev, [fileId]: `Maximum ${MAX_KEYWORDS} keywords allowed!` }));
      return;
    }

    const data = {
      file_name: selectedFile.file_name,
      envelope_id: Number(selectedFile.envelope_id),
      keyword: trimmedKeyword,
    };

    try {
      const response = await addSleeveFilesKeywords(data);
      console.log("Add Keyword Response:", response);
      if (response.status === 200 || response.data.status === "success") {
        setModalKeywords(prev => [...prev, trimmedKeyword]);
        setTagInputs(prev => ({ ...prev, [fileId]: "" }));
        setErrorMessages(prev => ({ ...prev, [fileId]: "" }));
        setFiles(prevFiles =>
          prevFiles.map(file =>
            file.id === fileId ? { ...file, keywords: [...file.keywords, trimmedKeyword] } : file
          )
        );
      } else {
        throw new Error(response.data.message || "Failed to add keyword");
      }
    } catch (error) {
      console.error("Error adding keyword:", error.message);
      setErrorMessages(prev => ({ ...prev, [fileId]: error.message || "Error adding keyword!" }));
    }
  };

  const removeKeyword = async (file, keyword) => {
    const data = {
      file_name: file.file_name, // Use the file parameter instead of selectedFile
      envelope_id: Number(file.envelope_id),
      keyword: keyword
    };

    try {
      const response = await deleteSleeveFilesKeywords(data);
      console.log("Delete Keyword Response:", response);
      if (response.status === 200 || response.data.status === "success") {
        setFiles(prevFiles =>
          prevFiles.map(f =>
            f.id === file.id ? { ...f, keywords: f.keywords.filter(k => k !== keyword) } : f
          )
        );
        if (tagModal && selectedFile?.id === file.id) {
          setModalKeywords(prev => prev.filter(k => k !== keyword));
        }
      } else {
        throw new Error(response.data.message || "Failed to remove keyword");
      }
    } catch (error) {
      console.error("Error removing keyword:", error.message);
      setErrorMessages(prev => ({ ...prev, [file.id]: error.message || "Error removing keyword!" }));
    }
  };

  const updateDocumentType = async () => {
    if (!docTypeInput.trim()) {
      setErrorMessages(prev => ({ ...prev, [selectedFile.id]: "Document type cannot be empty!" }));
      return;
    }

    const data = {
      file_id: selectedFile.id,
      document_type: docTypeInput
    };

    try {
      const response = await updateSleeveFilesDocumentsType(data);
      console.log("Update Document Type Response:", response);
      if (response.status === 200 || response.data.status === "success") {
        setFiles(prevFiles => {
          const updatedFiles = prevFiles.map(file =>
            file.id === selectedFile.id ? { ...file, document_type: docTypeInput } : file
          );
          console.log("Updated Files State:", updatedFiles);
          return updatedFiles;
        });
        setErrorMessages(prev => ({ ...prev, [selectedFile.id]: "" }));
        handleCloseDocTypeModal();
      } else {
        throw new Error(response.data.message || "API did not return success");
      }
    } catch (error) {
      console.error("Error updating document type:", error.message);
      setErrorMessages(prev => ({ ...prev, [selectedFile.id]: error.message || "Error updating document type!" }));
    }
  };

  return (
    <>
      {loading && (
        <div className="page-loading">
          <img src="/images/loader.gif" alt="loader" />
        </div>
      )}
      <div className="bg-white">
        <div style={{ overflowX: "auto", width: "100%", scrollbarWidth: "thin" }}>
          <table style={{ borderCollapse: "collapse", minWidth: "1450px", width: "100%", border: "1px solid #ddd" }}>
            <thead className="fs-5">
              <tr>
                <th style={{ width: "200px", border: "1px solid #ddd", padding: "8px", textAlign: "center" }}>Sleeve Name</th>
                <th style={{ width: "300px", border: "1px solid #ddd", padding: "8px", textAlign: "center" }}>File Name</th>
                <th style={{ width: "100px", border: "1px solid #ddd", padding: "8px", textAlign: "center" }}>File Size</th>
                <th style={{ width: "150px", border: "1px solid #ddd", padding: "8px", textAlign: "center" }}>Document Type</th>
                <th style={{ width: "350px", border: "1px solid #ddd", padding: "8px", textAlign: "center" }}>Keywords</th>
                <th style={{ width: "120px", border: "1px solid #ddd", padding: "8px", textAlign: "center" }}>Upload Date</th>
                <th style={{ width: "50px", border: "1px solid #ddd", padding: "8px", textAlign: "center" }}>
                  <i className="bi bi-three-dots-vertical" />
                </th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map(file => (
                <tr key={file.id}>
                  <td style={{ border: "1px solid #ddd", padding: "8px", textAlign: "center" }}>
                    {file.sleeve_name || "N/A"}
                  </td>
                  <td style={{ border: "1px solid #ddd", padding: "8px", textAlign: "center" }}>{file.file_name}</td>
                  <td style={{ border: "1px solid #ddd", padding: "8px", textAlign: "center" }}>
                    {(file.file_size / 1024).toFixed(2)} MB
                  </td>
                  <td style={{ border: "1px solid #ddd", padding: "8px", textAlign: "center" }}>
                    {file.document_type || "N/A"}
                  </td>
                  <td style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
                        {file.keywords.length > 0 ? (
                          file.keywords.slice(0, expandedRows[file.id] ? file.keywords.length : 2).map((keyword, idx) => (
                            <span key={idx} className="badge bg-primary text-white me-2 position-relative" style={{ padding: "5px 10px" }}>
                              {keyword}
                              <FaTimes className="cursor-pointer ms-1" onClick={() => removeKeyword(file, keyword)} />
                            </span>
                          ))
                        ) : (
                          <span>No keywords</span>
                        )}
                      </div>
                      {file.keywords.length > 2 && (
                        <span
                          className="show-more text-primary"
                          style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                          onClick={() => toggleExpand(file.id)}
                        >
                          {expandedRows[file.id] ? "Show Less" : `Show More (${file.keywords.length - 2})`}
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ border: "1px solid #ddd", textAlign: "center" }}>
                    {/* {file.upload_date("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })} */}
                  </td>
                  <td style={{ border: "1px solid #ddd", textAlign: "center" }}>
                    <div className="dropdown">
                      <span
                        className="functional_icon_ellipsis"
                        id={`dropdownMenuButton-${file.id}`}
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        style={{ cursor: "pointer" }}
                      >
                        <i className="bi bi-three-dots-vertical" />
                      </span>
                      <ul className="dropdown-menu" aria-labelledby={`dropdownMenuButton-${file.id}`}>
                        <li>
                          <span className="dropdown-item cur-pointer" onClick={() => handleOpenTagModal(file)}>
                            Add Tag
                          </span>
                        </li>
                        <li>
                          <span className="dropdown-item cur-pointer" onClick={() => handleOpenDocTypeModal(file)}>
                            Update Document Type
                          </span>
                        </li>
                      </ul>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Tag Modal */}
        {tagModal && selectedFile && (
          <div className="tag-modal-overlay" style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 }}>
            <div className="tag-modal-content" style={{ backgroundColor: "#fff", borderRadius: "8px", width: "500px" }}>
              <div className="tag-modal-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 20px" }}>
                <h4>Manage Tags</h4>
                <button onClick={handleCloseTagModal} style={{ border: "none", background: "none" }}>
                  <FaTimes />
                </button>
              </div>
              <hr style={{ margin: "0", border: "1px solid #ddd" }} />
              <div className="tag-modal-body" style={{ padding: "20px" }}>
                <div className="mb-3">
                  <strong>File:</strong> {selectedFile.file_name}
                </div>
                <div className="mb-3">
                  <strong className="me-2">Keywords ({modalKeywords.length}/{MAX_KEYWORDS}):</strong>
                  {modalKeywords.length > 0 ? (
                    modalKeywords.map((keyword, index) => (
                      <span key={index} className="badge bg-primary text-white me-2 mb-2">
                        {keyword}
                        <FaTimes className="cursor-pointer ms-1" onClick={() => removeKeyword(selectedFile, keyword)} />
                      </span>
                    ))
                  ) : (
                    <span className="text-muted">No keywords</span>
                  )}
                </div>
                <div className="form-group mb-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter tag name"
                    value={tagInputs[selectedFile.id] || ""}
                    onChange={e => setTagInputs(prev => ({ ...prev, [selectedFile.id]: e.target.value }))}
                    onKeyPress={handleKeyPress}
                  />
                </div>
                {errorMessages[selectedFile.id] && <p className="text-danger pb-2">{errorMessages[selectedFile.id]}</p>}
                <button className="btn btn-primary" onClick={() => saveKeyword(selectedFile.id, tagInputs[selectedFile.id])}>
                  Add
                </button>
              </div>
              <div className="tag-modal-footer" style={{ padding: "10px 20px", textAlign: "right" }}>
                <button onClick={handleCloseTagModal} className="btn btn-secondary">Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Document Type Update Modal */}
        {docTypeModal && selectedFile && (
          <div className="doc-type-modal-overlay" style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 }}>
            <div className="doc-type-modal-content" style={{ backgroundColor: "#fff", borderRadius: "8px", width: "500px" }}>
              <div className="doc-type-modal-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 20px" }}>
                <h4>Update Document Type</h4>
                <button onClick={handleCloseDocTypeModal} style={{ border: "none", background: "none" }}>
                  <FaTimes />
                </button>
              </div>
              <hr style={{ margin: "0", border: "1px solid #ddd" }} />
              <div className="doc-type-modal-body" style={{ padding: "20px" }}>
                <div className="mb-3">
                  <strong>File:</strong> {selectedFile.file_name}
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="docTypeInput" className="mb-2">Document Type:</label>
                  <input
                    type="text"
                    id="docTypeInput"
                    className="form-control mt-2"
                    value={docTypeInput}
                    onChange={e => setDocTypeInput(e.target.value)}
                    placeholder="Enter document type"
                  />
                </div>
                {errorMessages[selectedFile.id] && <p className="text-danger">{errorMessages[selectedFile.id]}</p>}
                <button className="btn btn-primary mt-3" onClick={updateDocumentType}>
                  Update
                </button>
              </div>
              <div className="doc-type-modal-footer" style={{ padding: "10px 20px", textAlign: "right" }}>
                <button onClick={handleCloseDocTypeModal} className="btn btn-secondary">Close</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Pagination Section */}
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="d-flex align-items-center">
          <p className="fw-bolder me-3 fs-5 text-white">Show</p>
          <select
            className="form-select"
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            style={{ width: "80px" }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        <Pagination>
          <Pagination.Prev
            onClick={() => setCurrentPage(prev => (prev > 1 ? prev - 1 : prev))}
            disabled={currentPage === 1}
          />
          {Array.from({ length: totalPages }, (_, i) => (
            <Pagination.Item
              key={i + 1}
              active={i + 1 === currentPage}
              onClick={() => handlePageChange(i + 1)}
            >
              {i + 1}
            </Pagination.Item>
          ))}
          <Pagination.Next
            onClick={() => setCurrentPage(prev => (prev < totalPages ? prev + 1 : prev))}
            disabled={currentPage === totalPages}
          />
        </Pagination>
      </div>
    </>
  );
};

export default MyFilesTable;